import { useCallback } from 'react'

import { addDays } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'

import type { TriageEventPayload } from '../../onboarding/types'

export type EmailType = '30_day' | '60_day'

export interface UseScheduleTraumaCheckinFollowUpsArgs {
  postTriageEvent: (payload: TriageEventPayload) => Promise<unknown>
  lyraId?: string | null
  onError?: (args: { emailType: EmailType; error: unknown }) => void
}

// Formats a Date to UTC string: yyyy-MM-dd HH:mm:ss
export const formatUtcYmdHms = (date: Date) => {
  // Use date-fns-tz to ensure formatting is done in UTC regardless of local timezone
  return formatInTimeZone(date, 'UTC', 'yyyy-MM-dd HH:mm:ss')
}

export const useScheduleTraumaCheckinFollowUps = ({
  postTriageEvent,
  lyraId,
  onError,
}: UseScheduleTraumaCheckinFollowUpsArgs) => {
  const scheduleTraumaCheckinFollowUps = useCallback(async () => {
    if (lyraId == null || lyraId === '') return
    const now = Date.now()

    const makePayload = (daysFromNow: 30 | 60) => {
      const emailType = `${daysFromNow}_day` as EmailType
      const dt = addDays(new Date(now), daysFromNow)
      return {
        signal_name: 'send_client_trauma_check_in_follow_up_email',
        delivery_time: formatUtcYmdHms(dt),
        // This arg is what causes idempotency on the backend
        enforce_unique_task_id: true,
        arguments: { email: emailType, lyra_id: lyraId },
        task_id: `${lyraId}-client-trauma-check-in-follow-up-email-${emailType}`,
      }
    }

    const req30 = postTriageEvent(makePayload(30))
    const req60 = postTriageEvent(makePayload(60))

    const results = await Promise.allSettled([req30, req60])
    results.forEach((r, idx) => {
      if (r.status === 'rejected') {
        const emailType: EmailType = idx === 0 ? '30_day' : '60_day'
        onError?.({ emailType, error: r.reason })
      }
    })
  }, [postTriageEvent, lyraId, onError])

  return { scheduleTraumaCheckinFollowUps }
}
